"use client";

import { useMemo } from "react";
import { usePathname } from "next/navigation";
import { defaultSEO } from "@/config/seoConfig";

function toAbsoluteUrl(pathOrUrl?: string): string {
  if (!pathOrUrl) {
    return `${defaultSEO.siteUrl}${defaultSEO.defaultOgImage}`;
  }

  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) {
    return pathOrUrl;
  }

  return pathOrUrl.startsWith("/")
    ? `${defaultSEO.siteUrl}${pathOrUrl}`
    : `${defaultSEO.siteUrl}/${pathOrUrl}`;
}

interface OgImageMetaProps {
  title?: string;
  ogImage?: string;
}

export default function OgImageMeta({ title, ogImage }: OgImageMetaProps) {
  const pathname = usePathname() || "";

  const resolvedImage = useMemo(() => {
    // 1) Explicit custom image provided by page/template
    if (ogImage) {
      return toAbsoluteUrl(ogImage);
    }

    const courseMatch = pathname.match(/^\/courses\/([^/?#]+)/);
    if (courseMatch?.[1]) {
      // 2) Course detail page image convention
      return toAbsoluteUrl(`/images/og/courses/${courseMatch[1]}.jpg`);
    }

    const blogMatch = pathname.match(/^\/blog\/([^/?#]+)/);
    if (blogMatch?.[1]) {
      // 3) Blog detail page image convention
      return toAbsoluteUrl(`/images/og/blogs/${blogMatch[1]}.jpg`);
    }

    // 4) Global default fallback
    return toAbsoluteUrl(defaultSEO.defaultOgImage);
  }, [ogImage, pathname]);

  const imageAlt = title || defaultSEO.defaultTitle;

  return (
    <>
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt} />
      <meta name="twitter:image" content={resolvedImage} />
    </>
  );
}

